import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwSectionBaseComponent,
  DwUiConfigRegistryService,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken
} from '@devwareapps/devware-cap';
import { combineLatest, of, Subject } from 'rxjs';
import { catchError, debounceTime, takeUntil } from 'rxjs/operators';

import {
  AppMetaDataItemNames,
  AssignmentStudentEntity,
  CourseEntity,
  CourseLessonEntity,
  CourseModuleEntity,
  LessonTypeAllItems,
  RoAssignmentStudentCourseLessonProgressEntity
} from '../../../../meta-data/app-meta-data.service';
import { LessonReferenceInfo } from '../../../courses/models/lesson-reference-info.model';
import { CourseNavigationService } from '../../../courses/services/course-navigation.service';
import { CourseRepositoryService } from '../../../courses/services/course-repository.service';

interface AssignmentStudentLessonRow {
  readonly id: number;
  readonly lessonReference: LessonReferenceInfo;
  readonly title: string;
  readonly typeLabel: string;
  readonly durationLabel: string;
  readonly statusLabel: string;
  readonly statusClass: string;
  readonly iconClass: string;
  readonly percentComplete: number;
}

interface AssignmentStudentModuleRow {
  readonly id: number;
  readonly title: string;
  readonly countLabel: string;
  readonly statusLabel: string;
  readonly percentComplete: number;
  readonly expanded: boolean;
  readonly lessons: AssignmentStudentLessonRow[];
}

@DwComponent({
  key: 'assignment-student-lesson-progress',
  name: 'Assignment Student Lesson Progress List',
  componentType: DwComponentType.formSection,
  parentItemName: [AppMetaDataItemNames.AssignmentStudent, AppMetaDataItemNames.Assignment],
})
@Component({
  selector: 'app-assignment-student-lesson-progress',
  templateUrl: './assignment-student-lesson-progress.component.html',
  styleUrls: ['./assignment-student-lesson-progress.component.scss']
})
export class AssignmentStudentLessonProgressComponent
  extends DwSectionBaseComponent
  implements OnInit, OnDestroy {
  assignmentStudent: AssignmentStudentEntity;
  formApi: DwMetaDataFormApi;

  moduleRows: AssignmentStudentModuleRow[] = [];
  loading = false;

  private course: CourseEntity;
  private loadedProgressRows: RoAssignmentStudentCourseLessonProgressEntity[] = [];
  private expandedModuleId?: number;
  private moduleExpansionInitialized = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    dwExpressionService: DwExpressionService,
    protected dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    private dwFormStateService: DwMetaDataFormStateService,
    private courseNavigationService: CourseNavigationService,
    private courseRepositoryService: CourseRepositoryService,
  ) {
    super(dwExpressionService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.runAfterFormDataLoaded(() => this.loadAssignedLessons());

    this.formApi.getFormDataChanges<AssignmentStudentEntity>()
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe(assignmentStudent => {
        this.assignmentStudent = assignmentStudent;
        this.loadAssignedLessons();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleModuleExpanded(moduleRow: AssignmentStudentModuleRow): void {
    this.expandedModuleId = moduleRow.expanded ? undefined : moduleRow.id;

    this.buildRows();
  }

  openLesson(lessonRow: AssignmentStudentLessonRow): void {
    this.courseNavigationService.navigateToLesson(lessonRow.lessonReference);
  }

  trackModule(index: number, moduleRow: AssignmentStudentModuleRow): number {
    return moduleRow.id;
  }

  trackLesson(index: number, lessonRow: AssignmentStudentLessonRow): number {
    return lessonRow.id;
  }

  private loadAssignedLessons(): void {
    this.assignmentStudent = this.assignmentStudent || this.formApi.getFormData<AssignmentStudentEntity>();

    const courseId = this.resolveCourseId(this.assignmentStudent);

    if (!courseId) {
      this.course = null;
      this.loadedProgressRows = [];
      this.buildRows();
      return;
    }

    const assignmentId = this.resolveAssignmentId(this.assignmentStudent);
    const courseOb = this.course?.CourseId === courseId
      ? of(this.course)
      : this.courseRepositoryService.getCourseHierarchy(courseId);
    const progressOb = assignmentId
      ? this.courseRepositoryService.loadAssignmentStudentLessonProgress(assignmentId).pipe(catchError(() => of([])))
      : of([]);

    this.loading = true;

    combineLatest([courseOb.pipe(catchError(() => of(null))), progressOb])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([course, loadedProgressRows]) => {
        this.course = course;
        this.loadedProgressRows = loadedProgressRows || [];
        this.loading = false;
        this.buildRows();
      });
  }

  private buildRows(): void {
    const assignedLessonIds = this.resolveAssignedLessonIds();
    const progressRows = this.resolveProgressRows();
    const moduleRows: AssignmentStudentModuleRow[] = [];

    for (const courseModule of this.course?.CourseModule || []) {
      const assignedLessons = (courseModule.CourseLesson || [])
        .filter(lesson => assignedLessonIds.indexOf(Number(lesson.CourseLessonId)) >= 0);

      if (!assignedLessons.length) {
        continue;
      }

      const lessonRows = assignedLessons.map(lesson => this.buildLessonRow(courseModule, lesson, progressRows));
      const percentComplete = this.calculateAveragePercent(lessonRows.map(lesson => lesson.percentComplete));

      moduleRows.push({
        id: courseModule.CourseModuleId,
        title: courseModule.ModuleName,
        countLabel: `${lessonRows.length} ${lessonRows.length === 1 ? 'lesson' : 'lessons'}`,
        statusLabel: this.buildModuleStatusLabel(percentComplete),
        percentComplete,
        expanded: this.isModuleExpanded(courseModule.CourseModuleId, moduleRows.length),
        lessons: lessonRows
      });
    }

    this.moduleRows = moduleRows;
  }

  private buildLessonRow(
    courseModule: CourseModuleEntity,
    lesson: CourseLessonEntity,
    progressRows: RoAssignmentStudentCourseLessonProgressEntity[]
  ): AssignmentStudentLessonRow {
    const progress = progressRows.find(row => this.sameId(row.CourseLessonId, lesson.CourseLessonId));
    const percentComplete = this.normalizePercent(progress?.PercentComplete || 0);
    const complete = progress?.LessonComplete || percentComplete >= 100;

    return {
      id: lesson.CourseLessonId,
      lessonReference: {
        lessonId: lesson.CourseLessonId,
        moduleId: courseModule.CourseModuleId,
        courseId: this.course.CourseId
      },
      title: lesson.LessonName,
      typeLabel: this.resolveLessonTypeLabel(lesson),
      durationLabel: this.resolveDurationLabel(lesson),
      statusLabel: this.resolveStatusLabel(complete, percentComplete),
      statusClass: this.resolveStatusClass(complete, percentComplete),
      iconClass: this.resolveIconClass(lesson, complete),
      percentComplete
    };
  }

  private resolveAssignedLessonIds(): number[] {
    const assignmentLessons = this.assignmentStudent?.Assignment?.AssignmentCourseLesson || [];
    const assignmentLessonIds = assignmentLessons
      .filter(assignmentLesson => assignmentLesson._isDeleted !== true && !!assignmentLesson.CourseLessonId)
      .map(assignmentLesson => Number(assignmentLesson.CourseLessonId));

    if (assignmentLessonIds.length) {
      return assignmentLessonIds;
    }

    return this.resolveProgressRows()
      .map(progress => Number(progress.CourseLessonId))
      .filter(courseLessonId => !isNaN(courseLessonId))
      .filter((courseLessonId, index, values) => values.indexOf(courseLessonId) === index);
  }

  private resolveProgressRows(): RoAssignmentStudentCourseLessonProgressEntity[] {
    const progressRows = [
      ...(this.assignmentStudent?.RoAssignmentStudentCourseLessonProgress || []),
      ...(this.assignmentStudent?.Assignment?.AssignmentIdRoAssignmentStudentCourseLessonProgress || []),
      ...(this.loadedProgressRows || [])
    ];

    return (progressRows || [])
      .filter(progress =>
        !this.assignmentStudent?.AssignmentStudentId
        || this.sameId(progress.AssignmentStudentId, this.assignmentStudent.AssignmentStudentId))
      .filter((progress, index, values) =>
        values.findIndex(existing => this.sameId(existing.CourseLessonId, progress.CourseLessonId)) === index);
  }

  private resolveCourseId(assignmentStudent: AssignmentStudentEntity): number {
    const courseId = assignmentStudent?.Assignment?.CourseId
      || assignmentStudent?.Assignment?.Course?.CourseId
      || this.resolveProgressRows()[0]?.CourseId;

    return courseId ? Number(courseId) : null;
  }

  private resolveAssignmentId(assignmentStudent: AssignmentStudentEntity): number {
    const assignmentId = assignmentStudent?.AssignmentId || assignmentStudent?.Assignment?.AssignmentId;

    return assignmentId ? Number(assignmentId) : null;
  }

  private isModuleExpanded(moduleId: number, moduleIndex: number): boolean {
    if (!this.moduleExpansionInitialized && moduleIndex === 0) {
      this.expandedModuleId = moduleId;
      this.moduleExpansionInitialized = true;

      return moduleIndex === 0;
    }

    return this.sameId(this.expandedModuleId, moduleId);
  }

  private resolveLessonTypeLabel(lesson: CourseLessonEntity): string {
    switch (lesson.LessonTypeId) {
      case LessonTypeAllItems.Quiz:
        return 'Quiz';
      case LessonTypeAllItems.Video:
        return 'Video';
      default:
        return lesson.LessonTypeIdDisplay || 'Lesson';
    }
  }

  private resolveDurationLabel(lesson: CourseLessonEntity): string {
    const durationSeconds = lesson.Video?.VideoDurationSeconds;

    if (!durationSeconds) {
      return '';
    }

    const minutes = Math.max(1, Math.round(durationSeconds / 60));

    return `${minutes} min`;
  }

  private resolveStatusLabel(complete: boolean, percentComplete: number): string {
    if (complete) {
      return 'Complete';
    }

    if (percentComplete > 0) {
      return `${percentComplete}% complete`;
    }

    return 'Not started';
  }

  private resolveStatusClass(complete: boolean, percentComplete: number): string {
    if (complete) {
      return 'assignment-student-lessons__status--complete';
    }

    if (percentComplete > 0) {
      return 'assignment-student-lessons__status--active';
    }

    return 'assignment-student-lessons__status--empty';
  }

  private resolveIconClass(lesson: CourseLessonEntity, complete: boolean): string {
    if (complete) {
      return 'fa-check';
    }

    return lesson.LessonTypeId === LessonTypeAllItems.Quiz ? 'fa-list-alt' : 'fa-play';
  }

  private buildModuleStatusLabel(percentComplete: number): string {
    if (percentComplete >= 100) {
      return 'Complete';
    }

    if (percentComplete > 0) {
      return `${percentComplete}%`;
    }

    return 'Not started';
  }

  private calculateAveragePercent(percentValues: number[]): number {
    if (!percentValues.length) {
      return 0;
    }

    return this.normalizePercent(percentValues.reduce((sum, value) => sum + value, 0) / percentValues.length);
  }

  private sameId(value1: number | string, value2: number | string): boolean {
    return Number(value1) === Number(value2);
  }

  private normalizePercent(percentComplete: number): number {
    return Math.max(0, Math.min(100, Math.round(percentComplete || 0)));
  }
}
